import { zodResolver } from '@hookform/resolvers/zod';
import { CircleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { StudyCalendarSection } from '@/clinical-study/calendar/StudyCalendarSection';
import { AtRiskVisitsSection } from '@/clinical-study/subjects/AtRiskVisitsSection';
import { StudySubjectsSection } from '@/clinical-study/subjects/StudySubjectsSection';
import { useStudySubjectList } from '@/clinical-study/subjects/useStudySubjectList';
import { canUse, useCallerPermissions } from '@/clinical-study/useCallerPermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';
import { usePendingWrite } from '@/lib/use-pending-write';
import { IdentifierCorrectionSection } from './IdentifierCorrectionSection';
import { StudyMembershipsSection } from './StudyMembershipsSection';
import { StudyTimepointsSection } from './StudyTimepointsSection';

type ClinicalStudy =
  paths['/v1/clinical-study/studies/{studyId}']['get']['responses'][200]['content']['application/json'];

type LoadState =
  { status: 'loading' } | { status: 'error' } | { status: 'ready'; study: ClinicalStudy };

const editClinicalStudySchema = z.object({
  name: z.string().min(1, 'Campo obbligatorio').max(200),
  description: z.string().max(2000).optional(),
});
type EditClinicalStudyForm = z.infer<typeof editClinicalStudySchema>;

/**
 * The study detail page (`/clinical-study/studies/:studyId`, Resolution R9): a name/description
 * edit header plus composed sections, mirroring `OrganizationDetailPage.tsx`'s own
 * header-plus-composed-sections shape — `StudyTimepointsSection`, `StudyMembershipsSection`, and
 * `IdentifierCorrectionSection`, in that order.
 *
 * SL-0045 Activity 9 follow-up: the edit form and the three administrator sections are now gated on
 * the caller's own module permissions (`useCallerPermissions`). Before Activity 9 this page was
 * reachable only by a study administrator, because the study fetch required
 * `clinical-study:study:manage`; once the fetch moved to `clinical-study:study:read`, an
 * investigator reached a page whose top form and three of whose sections 403 on every write. The
 * gates remove those, and nothing is reordered: the investigator's own two sections are already
 * last, so hiding the four above them puts their work first without changing the administrator's
 * page at all. `AtRiskVisitsSection` stays ungated — its read is `clinical-study:appointment:manage`,
 * which every operational role holds.
 *
 * #893: `StudyCalendarSection` follows the at-risk list for any role that reads subjects; it shares
 * the page's single subject-list read, skips the schedule and pool reads the caller's permissions
 * rule out, and opens a subject through the same drill-down as the at-risk list.
 */
export function ClinicalStudyDetailPage() {
  const { t } = useTranslation(['clinical-study', 'common']);
  const { studyId } = useParams<{ studyId: string }>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [saved, setSaved] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [drillDown, setDrillDown] = useState<{ subjectId: string; request: number } | null>(null);
  // #893: the at-risk list and the calendar both open a subject; each request is numbered so asking
  // again for the same subject still opens it.
  const openSubject = (subjectId: string) =>
    setDrillDown((previous) => ({ subjectId, request: (previous?.request ?? 0) + 1 }));
  // SL-0045 Activity 7: this page owns the SINGLE subject-list fetch and hands it to both consumers,
  // mirroring how `OrganizationDetailPage` owns the one `GET /v1/platform/users` its own two
  // sections share. Two local hook calls would mean two identical requests and two copies that
  // drift apart the moment one section creates a subject.
  const subjectList = useStudySubjectList(studyId ?? '');
  // SL-0045 Activity 9 follow-up: owned here for the same reason `subjectList` is — one read per
  // page load, one copy of the answer. Every gated section below consults it through `canUse`,
  // which fails closed while loading and on error.
  const callerPermissions = useCallerPermissions();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<EditClinicalStudyForm>({ resolver: zodResolver(editClinicalStudySchema) });
  const { pending, run } = usePendingWrite();

  useEffect(() => {
    if (studyId === undefined) {
      return;
    }

    setState({ status: 'loading' });
    apiClient
      .GET('/v1/clinical-study/studies/{studyId}', { params: { path: { studyId } } })
      .then(({ data, error, response }) => {
        if (error !== undefined || !response.ok || data === undefined) {
          setState({ status: 'error' });
          return;
        }
        setState({ status: 'ready', study: data });
        reset({ name: data.name, description: data.description ?? undefined });
      })
      .catch(() => setState({ status: 'error' }));
  }, [studyId, reset]);

  if (studyId === undefined) {
    return null;
  }

  const onSubmit = async (values: EditClinicalStudyForm) => {
    setSaved(false);
    setApiError(null);
    const description =
      values.description === '' || values.description === undefined ? null : values.description;
    const { data, error, response } = await apiClient.PATCH(
      '/v1/clinical-study/studies/{studyId}',
      {
        params: { path: { studyId } },
        body: { name: values.name, description },
      },
    );

    if (error !== undefined || !response.ok || data === undefined) {
      setApiError(error?.detail ?? t('writeError'));
      return;
    }

    setState({ status: 'ready', study: data });
    reset({ name: data.name, description: data.description ?? undefined });
    setSaved(true);
    toast.success(t('studyDetail.savedToast'));
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Link to="/clinical-study/studies" className="text-sm underline">
          {t('studyDetail.backToStudies')}
        </Link>
        {state.status === 'loading' && (
          <SkeletonRegion>
            <Skeleton className="my-[3px] h-5 w-64" />
          </SkeletonRegion>
        )}
        {state.status === 'error' && (
          <p className="text-destructive flex items-center gap-2 text-sm">
            <CircleAlert className="size-5 shrink-0" aria-hidden="true" />
            {t('studyDetail.loadError')}
          </p>
        )}
        {state.status === 'ready' && <h2 className="screen-title">{state.study.name}</h2>}
        <p className="text-sm">
          {t('studyDetail.idLabel')}: <code>{studyId}</code>
        </p>
      </div>

      {state.status === 'ready' && canUse(callerPermissions, 'clinical-study:study:manage') && (
        <form
          className="max-w-md space-y-4"
          onSubmit={(event) => void handleSubmit((values) => run(() => onSubmit(values)))(event)}
        >
          <h3 className="card-label">{t('studyDetail.editTitle')}</h3>
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="clinical-study-detail-name">
              {t('studies.nameLabel')}
            </label>
            <Input
              id="clinical-study-detail-name"
              {...register('name', { onChange: () => setSaved(false) })}
            />
            {errors.name && <p className="text-destructive text-sm">{errors.name.message}</p>}
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="clinical-study-detail-description">
              {t('studies.descriptionLabel')}
            </label>
            <Input
              id="clinical-study-detail-description"
              aria-describedby="clinical-study-detail-description-hint"
              {...register('description', { onChange: () => setSaved(false) })}
            />
            <p
              id="clinical-study-detail-description-hint"
              className="text-xs text-muted-foreground"
            >
              {t('studies.descriptionHint')}
            </p>
            {errors.description && (
              <p className="text-destructive text-sm">{errors.description.message}</p>
            )}
          </div>
          <Button type="submit" size="sm" disabled={!isDirty || pending}>
            {t('common:save')}
          </Button>
          {apiError && <p className="text-destructive text-sm">{apiError}</p>}
          {saved && <p className="text-success text-sm">{t('studyDetail.saved')}</p>}
        </form>
      )}

      {state.status === 'ready' && (
        <div className="space-y-6">
          <AtRiskVisitsSection studyId={studyId} onDrillDown={openSubject} />
          {canUse(callerPermissions, 'clinical-study:study-subject:read') && (
            <StudyCalendarSection
              studyId={studyId}
              subjectList={subjectList}
              canReadSchedules={canUse(callerPermissions, 'clinical-study:appointment:manage')}
              canReadPool={canUse(callerPermissions, 'clinical-study:treatment-date-pool:read')}
              onOpenSubject={openSubject}
            />
          )}
          {canUse(callerPermissions, 'clinical-study:study-timepoint:manage') && (
            <StudyTimepointsSection studyId={studyId} />
          )}
          {canUse(callerPermissions, 'clinical-study:study-membership:manage') && (
            <StudyMembershipsSection studyId={studyId} />
          )}
          {canUse(callerPermissions, 'clinical-study:study-subject-identifier:correct') && (
            <IdentifierCorrectionSection studyId={studyId} subjectList={subjectList} />
          )}
          <StudySubjectsSection
            studyId={studyId}
            subjectList={subjectList}
            openSubjectId={drillDown?.subjectId ?? null}
            openRequest={drillDown?.request ?? 0}
            canReadSchedules={canUse(callerPermissions, 'clinical-study:appointment:manage')}
            canReadPool={canUse(callerPermissions, 'clinical-study:treatment-date-pool:read')}
          />
        </div>
      )}
    </div>
  );
}
